import type { useI18n } from 'vue-i18n';
import type { BadgeConfiguration } from '@monolith/legacy/modules/promote-engage/touchpoints/services/badges-list.service';
import type { Amount } from '@client/monolith/core/types/amount';
import type { ImgCdnService } from '@client/modules/shared/util/img-cdn/img-cdn-service';
import { VariantType, QualificationEnum } from '@monolith/legacy/types/product';
import { getVariant } from '@monolith/legacy/services/features';
import { AkBadgeColor } from '@ankorstore/design-system';

import { getTags } from '@modules/core/services/metas/tag';
import { formatPrice } from '@monolith/core/utilities/price';

import { Builder } from './builder';
import { Brand } from '@client/modules/discovery/feature/recommendation/types';
import { ESProductHitBrand } from '@client/modules/discovery/domain/catalog/types';
import type { ItemIconConfig } from '@client/modules/discovery/ui/filters/types';

type ProductInputData = {
  id: number;
  name: string;
  brand?: Brand | ESProductHitBrand;
  description: string;
  images: string[];
  link: string;
  available_at: number;
  systemIcons?: ItemIconConfig[];
  variants: Array<{
    wholesale_price: Amount;
    retail_price: Amount;
    stock: {
      available_quantity: number;
      is_always_in_stock: boolean;
      inventory_policy?: 'deny' | 'continue';
      available_at?: number;
      status: string;
    };
    options: Array<{ name: VariantType }>;
    uuid: string;
    estimated_delivery?: {
      min_days: number | null;
      max_days: number | null;
      qualification?: QualificationEnum;
    };
  }>;
  brandName: string;
  brandLink: string;
  discountPercentage: number;
  discount_type: string;
  /** PRICE-2: when true, the simplified tile omits the discount badge from the overlay. */
  simplified?: boolean;
  unitMultiplier: number;
  tags: string[] | Record<string, string[]>;
  isNew: boolean;
  isProvenSuccess: boolean;
  ad?: { metadata: { ad_id: string; ad_type: string } };
  wholeSalePrice: Amount;
  originalWholeSalePrice: Amount;
  retailPrice: Amount;
  uuid: string;
  estimatedDelivery?: {
    min_days: number | null;
    max_days: number | null;
  };
};

export type ProductOutputData = {
  id: number;
  ad?: {
    metadata: {
      ad_id: string;
      ad_type: string;
    };
  };
  name: string;
  description: string;
  image: string;
  /** All product images (CDN-sized), primary first. Powers the PRICE-4 tile gallery; `image` remains the primary for legacy tiles. */
  images: string[];
  url: string;
  variantsLabel: string;
  brandName: string;
  brandUrl: string;
  price: string;
  retailPrice: string;
  unitsLabel: string;
  badges: BadgeConfiguration[];
  isSponsored: boolean;
  wholeSalePrice: Amount;
  originalWholeSalePrice: Amount;
  retail_price: Amount;
  brand: Brand | ESProductHitBrand;
  outOfStock: boolean;
  estimatedDelivery?: {
    minDays: number | null;
    maxDays: number | null;
  };
  isFastDelivery?: boolean;
  discountType: string;
  discountedPrice?: string;
  unitMultiplier: number;
  variants: ProductInputData['variants'];
  discountPercentage: number;
  uuid: string;
  systemIcons: ItemIconConfig[];
};

export class ProductBuilder extends Builder<ProductInputData, ProductOutputData> {
  constructor(
    private readonly imgCdnService: ImgCdnService,
    private readonly i18n: ReturnType<typeof useI18n>,
    private readonly getConvertedPrice: (price: Amount) => Promise<Amount>
  ) {
    super();
  }

  public async build() {
    return {
      id: this.data.id,
      ad: this.data.ad,
      name: this.data.name,
      description: this.data.description,
      image: this.imageUrl,
      images: this.imageUrls,
      url: this.data.link,
      variantsLabel: this.variantLabel,
      brandName: this.data.brandName,
      brandUrl: this.data.brandLink,
      price: await this.convertAndFormatPrice(this.price),
      discountedPrice: await this.convertAndFormatPrice(this.discountedPrice),
      discountType: this.data.discount_type,
      unitsLabel: this.unitsLabel,
      retailPrice: await this.convertAndFormatPrice(this.retailPrice),
      badges: this.badges,
      isSponsored: this.isSponsoredProduct,
      wholeSalePrice: this.data.wholeSalePrice,
      unitMultiplier: this.data.unitMultiplier,
      originalWholeSalePrice: this.data.originalWholeSalePrice,
      variants: this.data.variants,
      retail_price: this.data.retailPrice,
      discountPercentage: this.data.discountPercentage,
      uuid: this.data.uuid,
      brand: this.data.brand,
      outOfStock: !this.isInStock,
      estimatedDelivery: this.deliveryDays,
      isFastDelivery: this.isFastDelivery,
      systemIcons: this.data.systemIcons ?? [],
      showTrendingText: this.showTrendingText,
    };
  }

  private get imageUrl() {
    if (!this.data.images?.length) {
      return undefined;
    }

    return this.imgCdnService.imgCdnUrlFill(this.data.images[0], undefined, { w: '300', h: '300' });
  }

  private get imageUrls(): string[] {
    if (!this.data.images?.length) {
      return [];
    }

    return this.data.images.map((image) => this.imgCdnService.imgCdnUrlFill(image, undefined, { w: '300', h: '300' }));
  }

  private get variantLabel(): string {
    const variantsCount = this.data.variants?.length ?? 0;

    if (variantsCount < 2) {
      return undefined;
    }

    const variantType = this.data.variants[0].options?.[0]?.name;

    switch (variantType) {
      case 'size':
        return this.i18n.t('retailer.product-card.variant-size.lbl', variantsCount);
      case 'color':
        return this.i18n.t('retailer.product-card.variant-color.lbl', variantsCount);
      default:
        return this.i18n.t('retailer.product-card.variant.lbl', variantsCount);
    }
  }

  private get deliveryDays() {
    if (!this.data.variants?.length) {
      return undefined;
    }
    return {
      minDays: Math.min(...this.data.variants.map((v) => v.estimated_delivery?.min_days ?? 0)),
      maxDays: Math.max(...this.data.variants.map((v) => v.estimated_delivery?.max_days ?? 0)),
    };
  }

  private get isFastDelivery() {
    return this.data.variants?.some((variant) => variant.estimated_delivery?.qualification === QualificationEnum.FAST) ?? false;
  }

  private get price() {
    return this.data.originalWholeSalePrice;
  }

  // Always the product-level pair: `price` is the product's original_wholesale_price, so the
  // discounted price has to be the product's wholesale_price or the strikethrough describes a
  // different variant. Multi-variant products used to show the cheapest variant's price here,
  // which implied a discount the "-X%" label contradicted and disagreed with the product page.
  private get discountedPrice() {
    if (!this.data.discountPercentage) {
      return undefined;
    }
    return this.data.wholeSalePrice;
  }

  private get unitsLabel() {
    if (!this.data.unitMultiplier) {
      return undefined;
    }

    return this.i18n.t('Case of {count}', this.data.unitMultiplier);
  }

  private get retailPrice() {
    return this.data.retailPrice;
  }

  private get isBestseller() {
    return this.tags?.includes(getTags().tags_bestseller.label);
  }

  private get isProvenSuccess() {
    return this.data.isProvenSuccess;
  }

  private get isTrending() {
    return this.data.systemIcons?.some((i) => i.symbol === 'graph-up') ?? false;
  }

  private get showTrendingText() {
    return this.isTrending && !this.isProvenSuccess && !this.isBestseller;
  }

  private get badges() {
    const badges: BadgeConfiguration[] = [];
    const variant = getVariant('RET-3604');

    // PRICE-2: the simplified tile no longer shows the discount info in the badge overlay.
    if (!this.data.simplified && this.data.discountPercentage) {
      badges.push({
        priority: 1,
        discounts: [this.data.discountPercentage],
        color: AkBadgeColor.PurpleFull,
      });
    }

    if (!this.isInStock) {
      badges.push({
        overridesPriority: true,
        priority: 1,
        text: this.i18n.t('Out of stock'),
        color: AkBadgeColor.RedFull,
      });
    }

    if (this.data.isNew && !this.isBestseller) {
      badges.push({
        priority: 10,
        text: this.i18n.t('New!'),
        color: AkBadgeColor.Blue,
      });
    }

    if (['A', 'B'].includes(variant) && this.isProvenSuccess) {
      badges.push({
        priority: 10,
        symbol: 'arrow-repeat-check',
        text: this.i18n.t('discovery.product.badges.proven-success'),
        color: AkBadgeColor.GreenFull,
      });
    }

    if ((variant === 'A' && this.isBestseller && !this.isProvenSuccess) || (variant === 'C' && this.isBestseller)) {
      badges.push({
        priority: 100,
        text: this.i18n.t('discovery.product.badges.best-seller'),
        color: AkBadgeColor.Green,
      });
    }

    if (this.isTrending) {
      badges.push({
        priority: 5,
        symbol: 'graph-up',
        text: this.showTrendingText ? this.i18n.t('discovery.product.trending.label') : undefined,
        color: AkBadgeColor.Orange,
      });
    }

    return badges;
  }

  private get isInStock() {
    return this.data.variants?.some(
      ({ stock }) => stock.is_always_in_stock || stock?.inventory_policy === 'continue' || stock?.status !== 'out_of_stock',
    );
  }

  private async convertAndFormatPrice(price: Amount) {
    if (!price) {
      return undefined;
    }

    const convertedPrice = await this.getConvertedPrice(price);

    return formatPrice(convertedPrice, undefined, this.i18n.locale.value);
  }

  private get isSponsoredProduct() {
    return Boolean(this.data.ad?.metadata.ad_id);
  }

  private get tags() {
    if (Array.isArray(this.data.tags)) {
      return this.data.tags;
    }

    return this.data.tags?.[this.i18n.locale.value] ?? [];
  }
}
